<template>
  <div class="relative">
    <!-- Accentuated radial gradient behind badge -->
    <div
      class="absolute top-0 left-0 w-full h-[700px] pointer-events-none"
      :class="isExpired ? 'badge-radial-red' : 'badge-radial-blue'"
    ></div>

    <!-- Main Content -->
    <div
      class="relative z-10 max-w-7xl lg:max-w-[1600px] mx-auto px-6 lg:px-10 pt-16 lg:pt-36 pb-6"
    >
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
        <!-- Left Column - Badge + Description -->
        <div class="lg:col-span-7 flex-col">
          <!-- Badge Preview -->
          <div class="">
            <div class="badge-preview-container">
              <div ref="badgeContainerRef" class="badge-scale-lg">
                <TemplatePreview
                  class="w-full h-full"
                  ref="templatePreviewRef"
                  :templateImage="verifiableCredential.image"
                  :templateName="verifiableCredential.name"
                  :templateLocation="
                    verifiableCredential.type === 'Participation'
                      ? verifiableCredential.location?.split(', ')[0] ||
                        'Virtual'
                      : null
                  "
                  :templateStartDate="
                    verifiableCredential.type === 'Participation'
                      ? verifiableCredential.startDate
                      : null
                  "
                  :templateEndDate="
                    verifiableCredential.type === 'Participation'
                      ? verifiableCredential.endDate
                      : null
                  "
                  :templateThemeBgColor="
                    verifiableCredential.badgeTheme?.backgroundCardColor || null
                  "
                  :templateThemeTextColor="
                    verifiableCredential.badgeTheme?.textColor || null
                  "
                  :templateThemeEffect="
                    verifiableCredential.badgeTheme?.cardEffect || null
                  "
                  :templateThemeTexture="
                    verifiableCredential.badgeTheme?.textureImageURL || null
                  "
                  :templateThemeIsLogoBack="
                    verifiableCredential.badgeTheme?.isLogoBack || null
                  "
                  :templateThemeShape="
                    verifiableCredential.badgeTheme?.shape || null
                  "
                  :issuerName="issuerName"
                  :issuerImage="issuerImage"
                  :formatSm="false"
                  :type="verifiableCredential.type"
                  :is3d="true"
                  :padding="0"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-col items-center justify-center">
            <!-- Badge Name -->
            <h1
              class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 text-center pt-3"
            >
              {{ verifiableCredential.name }}
            </h1>

            <!-- Description -->
            <p
              v-if="verifiableCredential.description"
              class="text-gray-500 max-w-lg leading-relaxed mt-3 text-sm sm:text-base text-center"
            >
              {{ truncatedDescription }}
            </p>
            <button
              v-if="isDescriptionLong"
              @click="showFullDescription = !showFullDescription"
              class="text-[#3b82f6] font-medium text-sm mt-2 hover:text-[#2563eb] transition-colors"
            >
              {{ showFullDescription ? 'Show less' : 'Read more' }}
            </button>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-wrap justify-center gap-3 mt-8">
            <button
              @click="downloadBadge"
              class="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-sm text-sm font-medium text-gray-700"
            >
              <i class="fas fa-download text-gray-500"></i>
              Download
            </button>
            <button
              @click="shareBadge"
              class="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-sm text-sm font-medium text-gray-700"
            >
              <i class="fas fa-paper-plane text-gray-500"></i>
              Share
            </button>
          </div>
        </div>

        <!-- Right Column - Info Cards -->
        <div class="lg:col-span-5 space-y-5">
          <!-- Status + Issuer Information Card -->
          <div class="info-card rounded-2xl p-6 lg:p-8">
            <!-- Status -->
            <p
              class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3"
            >
              Status
            </p>
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center"
                :class="isExpired ? 'bg-red-50' : 'bg-blue-50'"
              >
                <i
                  class="text-xl"
                  :class="
                    isExpired
                      ? 'fas fa-times-circle text-red-500'
                      : 'fas fa-check-circle text-[#3b82f6]'
                  "
                ></i>
              </div>
              <span class="text-2xl font-bold text-gray-900">{{
                isExpired ? 'Expired' : 'Verified'
              }}</span>
            </div>

            <!-- Horizontal Divider -->
            <hr class="my-5 border-gray-200" />

            <!-- Issuer Information -->
            <p
              class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4"
            >
              Issuer Information
            </p>
            <a
              :href="
                verifiableCredential.issuer?.did
                  ? '/community/' + verifiableCredential.issuer.did
                  : '#'
              "
              class="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors"
            >
              <div class="flex items-center gap-3">
                <img
                  v-if="issuerImage"
                  :src="issuerImage"
                  class="w-10 h-10 rounded-lg object-cover border border-gray-200"
                  alt="Issuer logo"
                />
                <div
                  v-else
                  class="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center font-bold text-gray-500 text-xs"
                >
                  {{ issuerInitials }}
                </div>
                <div>
                  <div class="flex items-center gap-1.5">
                    <span class="font-bold text-sm text-gray-900">{{
                      issuerName
                    }}</span>
                    <i class="fas fa-check-circle text-[#3b82f6] text-xs"></i>
                  </div>
                  <p class="text-xs text-gray-500">
                    {{
                      verifiableCredential.type === 'Community'
                        ? 'Community Sender'
                        : 'Verified Issuer'
                    }}
                  </p>
                </div>
              </div>
              <span
                class="text-[#3b82f6] text-sm font-medium hover:underline shrink-0 ml-2"
                >Profile</span
              >
            </a>
          </div>

          <!-- Cryptographic Proof Card -->
          <div class="info-card rounded-2xl p-6 lg:p-8">
            <div class="flex items-center gap-2 mb-5">
              <i class="fas fa-shield-alt text-[#3b82f6]"></i>
              <p
                class="text-[10px] font-bold text-gray-400 uppercase tracking-widest"
              >
                Cryptographic Proof
              </p>
            </div>

            <!-- Credential Subject DID -->
            <div v-if="subjectDid" class="mb-5">
              <p
                class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2"
              >
                Credential Subject DID
              </p>
              <div
                class="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg border border-gray-100"
              >
                <code class="text-sm font-mono text-gray-600 truncate mr-2">{{
                  subjectDid
                }}</code>
                <!-- <button
                  @click="copyToClipboard(subjectDid)"
                  class="p-1.5 hover:bg-gray-200 rounded transition-colors shrink-0"
                >
                  <i class="fas fa-copy text-gray-400"></i>
                </button> -->
              </div>
            </div>

            <!-- Dates Row -->
            <div class="flex justify-between items-start">
              <div>
                <p
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1"
                >
                  Issued Date
                </p>
                <p class="text-sm font-bold text-gray-900">
                  {{ formatShortDate(verifiableCredential.created) }}
                </p>
              </div>
              <div class="text-right">
                <p
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1"
                >
                  Expires
                </p>
                <p
                  class="text-sm font-bold"
                  :class="isExpired ? 'text-red-500' : 'text-gray-900'"
                >
                  {{
                    verifiableCredential.expirationDate
                      ? formatShortDate(verifiableCredential.expirationDate)
                      : '—'
                  }}
                </p>
              </div>
            </div>
          </div>

          <!-- Badge Details Card -->
          <div class="info-card rounded-2xl p-6 lg:p-8">
            <div class="flex items-center gap-2 mb-4">
              <i class="fas fa-th-list text-gray-500"></i>
              <p
                class="text-[10px] font-bold text-gray-400 uppercase tracking-widest"
              >
                Badge Details
              </p>
            </div>

            <!-- Criteria -->
            <div v-if="verifiableCredential.criteria">
              <p
                class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2"
              >
                Criteria
              </p>
              <p class="text-sm text-gray-600 leading-relaxed">
                {{ verifiableCredential.criteria }}
              </p>
            </div>

            <!-- Dates for Participation -->
            <div
              v-if="verifiableCredential.type === 'Participation'"
              class="grid grid-cols-2 gap-4 mt-4"
            >
              <div v-if="verifiableCredential.startDate">
                <p
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1"
                >
                  Event Date
                </p>
                <p class="text-sm font-medium text-gray-900">
                  {{ formatShortDate(verifiableCredential.startDate) }}
                </p>
              </div>
              <div v-if="verifiableCredential.location">
                <p
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1"
                >
                  Location
                </p>
                <p class="text-sm font-medium text-gray-900">
                  {{ verifiableCredential.location || 'Virtual' }}
                </p>
              </div>
            </div>

            <!-- Community Endorsement -->
            <div
              v-if="
                verifiableCredential.type === 'Community' &&
                verifiableCredential.endorsement
              "
              class="mt-4"
            >
              <div
                class="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2"
              >
                <i class="fas fa-star text-[#3b82f6] text-sm"></i>
                <span class="text-sm font-medium text-blue-700"
                  >Approved by {{ verifiableCredential.endorsement.name }}</span
                >
              </div>
            </div>
          </div>

          <!-- Event Log Card -->
          <div class="event-log-section rounded-2xl p-6">
            <div class="flex items-center gap-2 mb-6">
              <i class="fas fa-clock text-gray-400"></i>
              <p
                class="text-xs font-bold text-gray-400 uppercase tracking-widest"
              >
                Event Log
              </p>
            </div>

            <div class="relative space-y-6 pl-6">
              <!-- Timeline line -->
              <div
                class="absolute left-[9px] -top-[5px] bottom-2 w-[2px] bg-gray-200 text-left mx-0.5 my-1"
              ></div>

              <!-- Credential Claimed -->
              <div class="relative">
                <div
                  class="absolute -left-[17px] top-1.5 w-2.5 h-2.5 rounded-full"
                  :class="isExpired ? 'bg-red-500' : 'bg-[#3b82f6]'"
                ></div>
                <div>
                  <h4 class="font-bold text-sm text-gray-900">
                    Credential Claimed
                  </h4>
                  <p class="text-xs text-gray-500 mt-0.5">
                    {{ formatEventDate(verifiableCredential.created) }}
                  </p>
                </div>
              </div>

              <!-- Minted on Blockchain -->
              <div class="relative">
                <div
                  class="absolute -left-[17px] top-1.5 w-2.5 h-2.5 bg-[#3b82f6] rounded-full"
                ></div>
                <div>
                  <h4 class="font-bold text-sm text-gray-900">
                    Minted on Blockchain
                  </h4>
                  <p class="text-xs text-gray-500 mt-0.5">
                    {{ formatEventDate(verifiableCredential.created) }}
                  </p>
                </div>
              </div>

              <!-- Issuer Signature Applied -->
              <div class="relative">
                <div
                  class="absolute -left-[17px] top-1.5 w-2.5 h-2.5 bg-[#3b82f6] rounded-full"
                ></div>
                <div>
                  <h4 class="font-bold text-sm text-gray-900">
                    Issuer Signature Applied
                  </h4>
                  <p class="text-xs text-gray-500 mt-0.5">
                    {{ formatEventDate(verifiableCredential.created) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Share Fallback Modal -->
    <Teleport to="body">
      <div
        v-if="showShareModal"
        class="fixed inset-0 z-50 flex items-center justify-center"
      >
        <div
          class="absolute inset-0 bg-black/40 backdrop-blur-sm"
          @click="showShareModal = false"
        ></div>
        <div
          class="relative bg-white rounded-2xl shadow-2xl p-6 w-[90%] max-w-md mx-4"
        >
          <button
            @click="showShareModal = false"
            class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i class="fas fa-times text-lg"></i>
          </button>

          <div class="flex items-center gap-3 mb-5">
            <div
              class="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center"
            >
              <i class="fas fa-share-alt text-[#3b82f6]"></i>
            </div>
            <h3 class="text-lg font-bold text-gray-900">Share this badge</h3>
          </div>

          <p class="text-sm text-gray-500 mb-4">
            The link has been copied to your clipboard. You can paste it
            anywhere to share.
          </p>

          <div
            class="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3"
          >
            <code class="text-sm text-gray-600 font-mono truncate flex-1">{{
              shareUrl
            }}</code>
            <button
              @click="copyToClipboard(shareUrl)"
              class="shrink-0 p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <i class="fas fa-copy text-gray-400"></i>
            </button>
          </div>

          <div
            class="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100"
          >
            <i class="fas fa-check-circle text-[#3b82f6] text-sm"></i>
            <span class="text-sm text-blue-600 font-medium"
              >Link copied to clipboard!</span
            >
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import TemplatePreview from './TemplatePreview.vue';
import { useRoute } from 'vue-router';
import { createToast } from 'mosha-vue-toastify';
import html2canvas from 'html2canvas';

export default {
  props: {
    vc: Object,
  },
  components: {
    TemplatePreview,
  },
  setup(props) {
    const verifiableCredential = ref(props.vc);
    const route = useRoute();
    const code = ref(route.query.code);
    const showFullDescription = ref(false);
    const templatePreviewRef = ref();
    const badgeContainerRef = ref();
    const showShareModal = ref(false);
    const shareUrl = ref(window.location.href);

    // Computed properties
    const issuerName = computed(() => {
      if (verifiableCredential.value.type === 'Community') {
        return verifiableCredential.value.templateIssuer?.name || 'Unknown';
      }
      return verifiableCredential.value.issuer?.name || 'Unknown';
    });

    const issuerImage = computed(() => {
      let image = '';
      if (verifiableCredential.value.type === 'Community') {
        image = verifiableCredential.value.templateIssuer?.profileImage || '';
      } else {
        image = verifiableCredential.value.issuer?.profileImage || '';
      }
      return image.replace(
        'https://ipfs.infura.io/ipfs/',
        'https://myntfsid.mypinata.cloud/ipfs/'
      );
    });

    const issuerInitials = computed(() => {
      const name = issuerName.value;
      if (!name || name === 'Unknown') return '?';
      return name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    });

    const templateLabel = computed(() => {
      switch (verifiableCredential.value.type) {
        case 'Basic':
          return 'Certified Badge';
        case 'Community':
          return 'Community Badge';
        case 'Participation':
          return 'Participation Badge';
        case 'Membership':
          return 'Membership Card';
        case 'Ticket':
          return 'Event Ticket';
        case 'Role':
          return 'Role Badge';
        default:
          return 'Badge';
      }
    });

    const isExpired = computed(() => {
      if (!verifiableCredential.value.expirationDate) return false;
      return new Date(verifiableCredential.value.expirationDate) < new Date();
    });

    const isTimestampValid = computed(() => {
      if (!props.vc.timestamp) return false;
      const diff =
        Math.abs(
          new Date(props.vc.timestamp * 1000) -
            new Date(verifiableCredential.value.created)
        ) / 1000;
      return diff < 120;
    });

    const subjectDid = computed(() => {
      return (
        verifiableCredential.value.credentialSubject?.id ||
        verifiableCredential.value.subject?.id ||
        verifiableCredential.value.issuer?.did ||
        null
      );
    });

    const isDescriptionLong = computed(() => {
      const desc = verifiableCredential.value.description;
      return desc && desc.length > 150;
    });

    const truncatedDescription = computed(() => {
      const desc = verifiableCredential.value.description;
      if (!desc) return '';
      if (showFullDescription.value || desc.length <= 150) return desc;
      return desc.slice(0, 150).trim() + '...';
    });

    // Helper functions
    const formatShortDate = (dateStr) => {
      if (!dateStr) return '—';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      } catch {
        return dateStr;
      }
    };

    const formatEventDate = (dateStr) => {
      if (!dateStr) return '—';
      try {
        const date = new Date(dateStr);
        const datePart = date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        const timePart = date.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
        });
        return `${datePart} \u2022 ${timePart}`;
      } catch {
        return dateStr;
      }
    };

    const copyToClipboard = (text) => {
      if (navigator.clipboard && window.isSecureContext && text) {
        navigator.clipboard.writeText(text).then(
          () => {
            createToast('Copied to clipboard', {
              type: 'success',
              showIcon: true,
              position: 'top-right',
              toastBackgroundColor: '#050931',
            });
          },
          (err) => {
            console.error('Could not copy text: ', err);
          }
        );
      }
    };

    const shareBadge = async () => {
      const url = window.location.href;
      shareUrl.value = url;

      if (navigator.share) {
        try {
          await navigator.share({
            title: verifiableCredential.value.name,
            text: `Check out this verified badge: ${verifiableCredential.value.name}`,
            url: url,
          });
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('Share failed:', err);
          }
        }
      } else {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        }
        showShareModal.value = true;
      }
    };

    const downloadBadge = async () => {
      try {
        const container = badgeContainerRef.value;
        if (!container) {
          throw new Error('Badge container not found');
        }

        const canvas = await html2canvas(container, {
          useCORS: true,
          allowTaint: true,
          scale: 2,
          backgroundColor: null,
          logging: false,
        });

        const dataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${verifiableCredential.value.name.replace(
          /[^a-z0-9]/gi,
          '_'
        )}_${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('Capture failed:', err);
        createToast('Failed to download badge image', {
          type: 'danger',
          showIcon: true,
          position: 'top-right',
          toastBackgroundColor: '#050931',
        });
      }
    };

    return {
      verifiableCredential,
      issuerName,
      issuerImage,
      issuerInitials,
      templateLabel,
      isExpired,
      isTimestampValid,
      subjectDid,
      isDescriptionLong,
      truncatedDescription,
      showFullDescription,
      code,
      formatShortDate,
      formatEventDate,
      copyToClipboard,
      shareBadge,
      showShareModal,
      shareUrl,
      downloadBadge,
      templatePreviewRef,
      badgeContainerRef,
    };
  },
};
</script>

<style scoped>
.info-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(230, 230, 230, 0.6);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.event-log-section {
  background: rgba(239, 246, 255, 0.6);
  border: 1px solid rgba(191, 219, 254, 0.5);
}

.badge-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 380px;
  text-align: left;
}

@media (min-width: 1024px) {
  .badge-preview-container {
    min-height: 580px;
  }
  .badge-scale-lg {
    transform: scale(1.3);
    transform-origin: center center;
  }
}

/* Radial gradient - blue (valid) */
.badge-radial-blue {
  background: radial-gradient(
    ellipse 60% 70% at 35% 30%,
    rgba(147, 197, 253, 0.5) 0%,
    rgba(147, 197, 253, 0.25) 30%,
    rgba(191, 219, 254, 0.1) 60%,
    transparent 100%
  );
}

/* Radial gradient - red (expired) */
.badge-radial-red {
  background: radial-gradient(
    ellipse 60% 70% at 35% 30%,
    rgba(254, 202, 202, 0.5) 0%,
    rgba(254, 202, 202, 0.25) 30%,
    rgba(254, 226, 226, 0.1) 60%,
    transparent 100%
  );
}

@media (max-width: 640px) {
  .badge-preview-container {
    min-height: 300px;
  }
}
</style>
